import { MAX_ZNAKOW_SCENARIUSZA, czyPoziomTreningu } from "@shared/treningTypy.js";
import type { PoziomTreningu, PoziomTreninguDto } from "@shared/treningTypy.js";
import { kluczCwiczenia, opisPoziomu, type GrupaCwiczen } from "../../lib/trening.js";
import { Banner } from "../ui/Banner.js";
import { RadioGroup } from "../ui/Choice.js";
import { Textarea } from "../ui/Field.js";
import { SegmentedControl } from "../ui/SegmentedControl.js";
import { LicznikZnakow } from "./LicznikZnakow.js";

/**
 * Wnetrza trzech kart wyboru na ekranie "Ćwiczenia": cwiczenie, poziom trudnosci, rozmowca. Karty (tytuly "1. ...",
 * "2. ...") sklada strona, bo to ona rozstrzyga stany katalogu (ladowanie, blad, pusty): S-5 mowi, ze stany mieszkaja w
 * tej samej karcie co dane.
 */

/**
 * Cwiczenia pogrupowane wg parametru. Kazda grupa to osobna lista wyboru z nazwa parametru nad nia, ale wszystkie dziela
 * `name`, wiec to jeden wybor: zaznaczenie cwiczenia w jednym parametrze odznacza poprzednie, takze w innym.
 */
export function WyborCwiczenia({
  grupy,
  wybrane,
  onWybierz,
}: {
  grupy: readonly GrupaCwiczen[];
  /** Klucz wybranego cwiczenia (kluczCwiczenia). */
  wybrane: string | null;
  onWybierz: (klucz: string) => void;
}): React.JSX.Element {
  return (
    <div className="trening-wybor">
      {grupy.map((grupa) => (
        <RadioGroup
          key={grupa.lensSlug}
          etykieta={grupa.parametr}
          nazwa="trening-cwiczenie"
          wartosc={wybrane ?? ""}
          opcje={grupa.cwiczenia.map((c) => ({ wartosc: kluczCwiczenia(c), etykieta: c.tekst }))}
          onZmiana={onWybierz}
        />
      ))}
    </div>
  );
}

/** Piec poziomow z katalogu i pod nimi opis wybranego (baner informacyjny: trwajacy stan wyboru, BN-1). */
export function WyborPoziomu({
  poziomy,
  poziom,
  onZmiana,
}: {
  poziomy: readonly PoziomTreninguDto[];
  poziom: PoziomTreningu;
  onZmiana: (poziom: PoziomTreningu) => void;
}): React.JSX.Element {
  const opis = opisPoziomu(poziomy, poziom);
  return (
    <>
      <SegmentedControl
        etykieta="Poziom trudności"
        opcje={poziomy.map((p) => ({ wartosc: String(p.poziom), etykieta: p.nazwa }))}
        wartosc={String(poziom)}
        onZmiana={(wartosc) => {
          const numer = Number(wartosc);
          if (czyPoziomTreningu(numer)) onZmiana(numer);
        }}
      />
      {opis && (
        <div className="trening-opis-poziomu">
          <Banner wariant="info" tekst={opis} />
        </div>
      )}
    </>
  );
}

const ID_SCENARIUSZA = "trening-scenariusz";

/**
 * Opcjonalny opis rozmowcy. Pusty znaczy "typowy rozmowca": serwer dostaje pole tylko wtedy, gdy cos wpisano.
 *
 * Podpowiedz zostaje przy przykladzie z rola i prosi o brak prawdziwych imion, nazwisk i nazw firm: opis idzie do modelu
 * przy otwarciu rozmowy i przy kazdej turze, a w bazie zostaje bezterminowo (docs/trening.md, "Prywatnosc").
 */
export function OpisRozmowcy({
  wartosc,
  onZmiana,
}: {
  wartosc: string;
  onZmiana: (tekst: string) => void;
}): React.JSX.Element {
  return (
    <div className="trening-rozmowca">
      <Textarea
        id={ID_SCENARIUSZA}
        etykieta="Opis rozmówcy"
        value={wartosc}
        rows={3}
        maxLength={MAX_ZNAKOW_SCENARIUSZA}
        placeholder="Opisz rozmówcę własnymi słowami"
        hint="Z kim rozmawiasz? Na przykład: dyrektor firmy produkcyjnej, który sceptycznie patrzy na wdrożenie AI. Nie wpisuj prawdziwych imion, nazwisk ani nazw firm."
        aria-describedby={`${ID_SCENARIUSZA}-hint ${ID_SCENARIUSZA}-licznik`}
        onChange={(e) => onZmiana(e.target.value)}
      />
      <LicznikZnakow id={`${ID_SCENARIUSZA}-licznik`} dlugosc={wartosc.length} max={MAX_ZNAKOW_SCENARIUSZA} />
    </div>
  );
}
